import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Flex,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    SimpleGrid,
    Spinner,
    useBreakpointValue,
    useDisclosure
} from '@chakra-ui/react'
import {useProducts} from '@salesforce/commerce-sdk-react'

import GDIProductTileBlock from '../gdiProductTileBlock'
import HorizontalProductItem from '../../common/horizontalProductItem'
import styles from './products-popover-style'
import WebDrawer from '../../../../web-drawer'

/**
 * Renders products listed via comma-separated `ProductId` either inside a
 * desktop popover anchored to the CTA, or a mobile bottom drawer (85vh).
 *
 * @param {string[]} productIds - Parsed list of SKUs/IDs from GDILinkBlock.ProductId
 * @param {React.ReactElement} trigger - The CTA button. Cloned to attach popover/drawer triggers.
 */
const ProductsPopover = ({productIds, trigger}) => {
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const {isOpen, onOpen, onClose, onToggle} = useDisclosure()
    const baseStyle = styles.baseStyle

    const idsParam = productIds.join(',')
    const {data: productsData, isLoading} = useProducts(
        {
            parameters: {
                ids: idsParam,
                perPricebook: true,
                allImages: true,
                expand: ['promotions', 'variations', 'prices', 'images']
            }
        },
        {enabled: !!idsParam && isOpen}
    )

    /* Re-order fetched products to match the authored sequence (the API does
     * not guarantee response order matches the request `ids`). */
    const products = useMemo(() => {
        const map = new Map((productsData?.data || []).map((p) => [p.id, p]))
        return productIds.map((id) => map.get(id)).filter(Boolean)
    }, [productsData, productIds])

    /* Mobile: WebDrawer (size="85vh") with a 2-col grid. Reuse
     * GDIProductTileBlock so the tile UI matches the rest of the storefront
     * (badges, wishlist, etc.). */
    if (!isDesktop) {
        const drawerTrigger = React.cloneElement(trigger, {
            onClick: (e) => {
                trigger.props.onClick?.(e)
                onOpen()
            }
        })
        return (
            <>
                {drawerTrigger}
                <WebDrawer
                    isOpen={isOpen}
                    onClose={onClose}
                    size="85vh"
                    hideHeaderBorderBottom={true}
                >
                    {isLoading && !products.length ? (
                        <Flex {...baseStyle.drawerLoading}>
                            <Spinner />
                        </Flex>
                    ) : (
                        <SimpleGrid {...baseStyle.drawerGrid}>
                            {products.map((product) => (
                                <GDIProductTileBlock key={product.id} Product={product} />
                            ))}
                        </SimpleGrid>
                    )}
                </WebDrawer>
            </>
        )
    }

    /* Desktop: popover above the CTA, left-aligned with it (placement
     * top-start), 8px offset, fixed 325x391, scrollable. Click-toggle. */
    return (
        <Popover
            isOpen={isOpen}
            onClose={onClose}
            placement="top-start"
            offset={[0, 8]}
            isLazy
        >
            <PopoverTrigger>
                {React.cloneElement(trigger, {
                    onClick: (e) => {
                        trigger.props.onClick?.(e)
                        onToggle()
                    }
                })}
            </PopoverTrigger>
            <PopoverContent {...baseStyle.popoverContent}>
                <PopoverBody {...baseStyle.popoverBody}>
                    {isLoading && !products.length ? (
                        <Flex {...baseStyle.popoverLoading}>
                            <Spinner />
                        </Flex>
                    ) : (
                        <Flex {...baseStyle.popoverList}>
                            {products.map((product) => (
                                <HorizontalProductItem key={product.id} product={product} />
                            ))}
                        </Flex>
                    )}
                </PopoverBody>
            </PopoverContent>
        </Popover>
    )
}

ProductsPopover.propTypes = {
    productIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    trigger: PropTypes.element.isRequired
}

export default ProductsPopover
